<template>
    <div v-if="visible" class="lightbox" @mousedown="startDrag" @touchstart="startTouch" @click.self="close">
      <img
        :src="src"
        class="lightbox-image"
        :style="{
          transform: `scale(${zoom}) translate(${translateX}px, ${translateY}px)`,
          cursor: dragging ? 'grabbing' : 'grab'
        }"
        @wheel.prevent="onWheel"
        @mousemove="onDrag"
        @mouseup="endDrag"
        @mouseleave="endDrag"
        @touchmove="onTouchMove"
        @touchend="endTouch"
      />
      <button class="close-btn" @click="close">×</button>
    </div>
  </template>
  
  <script setup>
  import { ref, defineExpose } from "vue";
  
  const visible = ref(false);
  const src = ref("");
  const zoom = ref(1);
  const translateX = ref(0);
  const translateY = ref(0);
  
  const dragging = ref(false);
  let startX = 0, startY = 0;
  let lastX = 0, lastY = 0;
  
  function open(imageUrl) {
    src.value = imageUrl;
    zoom.value = 1;
    translateX.value = 0;
    translateY.value = 0;
    visible.value = true;
  }
  
  function close() {
    visible.value = false;
  }
  
  // Mouse Wheel Zoom
  function onWheel(e) {
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    zoom.value = Math.max(1, zoom.value + delta);
  }
  
  // Mouse Drag
  function startDrag(e) {
    dragging.value = true;
    startX = e.clientX;
    startY = e.clientY;
  }
  
  function onDrag(e) {
    if (!dragging.value) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    translateX.value = lastX + dx;
    translateY.value = lastY + dy;
  }
  
  function endDrag() {
    dragging.value = false;
    lastX = translateX.value;
    lastY = translateY.value;
  }
  
  // Touch Move
  let pinchStartDistance = null;
  let initialZoom = 1;
  
  function startTouch(e) {
    if (e.touches.length === 2) {
      pinchStartDistance = getDistance(e.touches);
      initialZoom = zoom.value;
    } else if (e.touches.length === 1) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }
  }
  
  function onTouchMove(e) {
    if (e.touches.length === 2) {
      const newDistance = getDistance(e.touches);
      const scale = newDistance / pinchStartDistance;
      zoom.value = Math.max(1, initialZoom * scale);
    } else if (e.touches.length === 1) {
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      translateX.value = lastX + dx;
      translateY.value = lastY + dy;
    }
  }
  
  function endTouch() {
    lastX = translateX.value;
    lastY = translateY.value;
    pinchStartDistance = null;
  }
  
  function getDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  defineExpose({ open });
  </script>
  
  <style scoped>
  .lightbox {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    touch-action: none; /* prevents default pinch/scroll */
  }
  
  .lightbox-image {
    max-width: 90%;
    max-height: 90%;
    transition: transform 0.1s ease;
    user-select: none;
  }
  
  .close-btn {
    position: absolute;
    top: 20px;
    right: 30px;
    font-size: 3rem;
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    z-index: 9999;
  }
  </style>
  